import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import './index.css';
import { initializeSharedArrayBufferCheck } from './utils/sharedArrayBufferUtils.js';

import App from './App.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';
import { AuthProvider } from './contexts/AuthContext.jsx';
import { MembershipProvider } from './contexts/MembershipContext.jsx';

// Eagerly import the AuthCallbackPage to ensure it's available immediately
import AuthCallbackPage from './pages/AuthCallbackPage.jsx';

// Lazy load other page components
const HomePage = lazy(() => import('./pages/HomePage.jsx'));
const LoginPage = lazy(() => import('./pages/LoginPage.jsx'));
const InterviewerPage = lazy(() => import('./pages/InterviewerPage.jsx'));
const DashboardPage = lazy(() => import('./pages/DashboardPage.jsx'));
const MembershipPage = lazy(() => import('./pages/MembershipPage.jsx'));
const JoinPage = lazy(() => import('./pages/JoinPage.jsx'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage.jsx'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage.jsx'));
const SupportPage = lazy(() => import('./pages/SupportPage.jsx'));
const AuthPage = lazy(() => import('./pages/AuthPage.jsx'));
const AuthenticatedHomePage = lazy(() => import('./pages/AuthenticatedHomePage.jsx'));
const MeetSession = lazy(() => import('./pages/MeetSession.jsx'));
const TeamPage = lazy(() => import('./pages/TeamPage.jsx'));
const TeamInvitePage = lazy(() => import('./pages/TeamInvitePage.jsx'));
const SettingsPage = lazy(() => import('./pages/SettingsPage.jsx'));
const BlogListPage = lazy(() => import('./pages/BlogListPage.jsx'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage.jsx'));
const AdminDashboardPage = lazy(() => import('./components/admin/AdminDashboardPage.jsx'));
const AdminRoute = lazy(() => import('./components/AdminRoute.jsx'));
const BillingSuccessPage = lazy(() => import('./pages/BillingSuccessPage.jsx'));
const BillingCancelPage = lazy(() => import('./pages/BillingCancelPage.jsx'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-gray-600">Loading...</div>
  </div>
);


const routes = [
  {
    path: '/',
    element: <App />,
    errorElement: <NotFoundPage />,
    children: [
      { index: true, element: <Suspense fallback={<LoadingFallback />}><HomePage /></Suspense> },
      { path: 'join/:sessionId', element: <Suspense fallback={<LoadingFallback />}><JoinPage /></Suspense> },
      { path: 'blog', element: <Suspense fallback={<LoadingFallback />}><BlogListPage /></Suspense> },
      { path: 'blog/:slug', element: <Suspense fallback={<LoadingFallback />}><BlogPostPage /></Suspense> },
      { path: 'terms', element: <Suspense fallback={<LoadingFallback />}><TermsOfServicePage /></Suspense> },
      { path: 'privacy', element: <Suspense fallback={<LoadingFallback />}><PrivacyPolicyPage /></Suspense> },
      { path: 'support', element: <Suspense fallback={<LoadingFallback />}><SupportPage /></Suspense> },
      { path: 'auth', element: <Suspense fallback={<LoadingFallback />}><AuthPage /></Suspense> },
      { path: 'auth/callback', element: <AuthCallbackPage /> },
      { path: 'app', element: <Suspense fallback={<LoadingFallback />}><AuthenticatedHomePage /></Suspense> },
      { path: 'login', element: <Suspense fallback={<LoadingFallback />}><LoginPage /></Suspense> },
      { path: 'dashboard', element: <Suspense fallback={<LoadingFallback />}><DashboardPage /></Suspense> },
      { path: 'membership', element: <Suspense fallback={<LoadingFallback />}><MembershipPage /></Suspense> },
      { path: 'billing/success', element: <Suspense fallback={<LoadingFallback />}><BillingSuccessPage /></Suspense> },
      { path: 'billing/cancel', element: <Suspense fallback={<LoadingFallback />}><BillingCancelPage /></Suspense> },
      { path: 'team', element: <Suspense fallback={<LoadingFallback />}><TeamPage /></Suspense> },
      { path: 'team/invite', element: <Suspense fallback={<LoadingFallback />}><TeamInvitePage /></Suspense> },
      { path: 'interviewer/:sessionId', element: <Suspense fallback={<LoadingFallback />}><InterviewerPage /></Suspense> },
      { path: 'meet/:sessionId', element: <Suspense fallback={<LoadingFallback />}><MeetSession /></Suspense> },
      { path: 'settings/*', element: <Suspense fallback={<LoadingFallback />}><SettingsPage /></Suspense> },
      { path: 'operations-control-center', element: <Suspense fallback={<LoadingFallback />}><AdminRoute><AdminDashboardPage /></AdminRoute></Suspense> },
    ],
  },
];

const router = createBrowserRouter(routes);

// Initialize SharedArrayBuffer checking
initializeSharedArrayBufferCheck();

// Unregister any existing service workers to prevent CORP errors with Firebase Storage
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    for (const registration of registrations) {
      registration.unregister().then(success => {
        if (success) {
          console.log('[Service Worker] Unregistered successfully:', registration.scope);
        }
      });
    }
  });
}

createRoot(document.getElementById('root')).render(
    <AuthProvider>
      <MembershipProvider>
        <RouterProvider router={router} />
      </MembershipProvider>
    </AuthProvider>
);
